import { setPhaseStandards } from "../lib/schedule";
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { translations } from "../i18n";
import { TEAMS, PHASES } from "../data/constants";
import { inUserTeam } from "../lib/teamScope";
import api, { setToken, authStore } from "../lib/api";
import { broadcastLogout, clearAuthStorageForUser, isLogoutForUser } from "../lib/crossTabLogout";
import { setI18nLang, startDomI18n, subscribeI18n, normLang, LANGS } from "../lib/i18n";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(authStore.get("ali_user")) || null; } catch { return null; }
  });
  const [lang, setLangState] = useState(() => normLang(user?.uiLang || localStorage.getItem("ali_lang")));
  const setLang = useCallback((l) => setLangState((cur) => normLang(typeof l === "function" ? l(cur) : l)), []);
  const langHydrated = useRef(!user);
  setI18nLang(lang);
  const [i18nTick, setI18nTick] = useState(0);
  useEffect(() => { startDomI18n(); return subscribeI18n(() => setI18nTick((n) => n + 1)); }, []);
  const [projects, setProjects] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [salesTargets, setSalesTargets] = useState({});
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(false);
  // 撤销历史栈：从 sessionStorage 恢复，让「同一标签页刷新」后仍可撤销（关闭标签/换标签则清空）。
  // 需求背景：原实现 history 只存在内存，页面一刷新记录就全没了，用户以为撤销坏了；改存 sessionStorage 后刷新保留。
  // 关键取舍：用 sessionStorage（而非 localStorage）——撤销是「最近操作后悔药」，跨会话/跨设备保留旧快照反而
  //   容易在数据已被他人改动后产生误导性回滚，sessionStorage「关标签即清」的语义更贴合。
  // 容错：读取/解析失败（隐私模式禁用存储、历史格式变更等）一律降级为空数组，绝不让它阻断应用启动。
  const HISTORY_KEY = "ali_undo_history";
  const [history, setHistory] = useState(() => {
    try { const raw = sessionStorage.getItem(HISTORY_KEY); const arr = raw ? JSON.parse(raw) : []; return Array.isArray(arr) ? arr : []; } catch { return []; }
  });
  const [permDenied, setPermDenied] = useState({});
  const [permGranted, setPermGranted] = useState({});

  const [myMods, setMyMods] = useState({ allowed: [], denied: [], keys: [], roleAllowed: [], roleDenied: [] });
  // 【2026-10-24 权限快照】服务端把当前账号在每个权限点（页面 / 子栏目 / 动作按钮）上的最终判定
  // 一次算好下发（含「子键继承父页面」结果）。菜单可见性、路由守卫与按钮可见性全部读这份快照，
  // 与后端 ensure_perm 接口准入同源 —— 「配了权限就能从菜单到页面到接口到按钮全链路生效」的数据基础。
  // null = 快照尚未加载或后端未提供（此时全部判定退回原有「角色名单 + modAllowed」前端逻辑，行为不变）。
  const [permMap, setPermMap] = useState(null);
  // 有已登录账号时，首屏必须等权限快照请求完成再作代操作路由判定；未登录页无需等待。
  const [permissionsLoading, setPermissionsLoading] = useState(() => !!user);
  const permRequestIdRef = useRef(0);
  const permLoadWarnedRef = React.useRef(false); // 加载失败只告警一次：60s 轮询失败不该刷屏，但不能像过去那样完全静默

  // 切换账号时丢弃旧账号快照，避免旧权限在新账号请求期间参与菜单与路由判断。
  const resetPermissionSnapshot = useCallback((loading = true) => {
    permRequestIdRef.current += 1;
    setPermissionsLoading(loading);
    setPermDenied({});
    setPermGranted({});
    setMyMods({ allowed: [], denied: [], keys: [], roleAllowed: [], roleDenied: [] });
    setPermMap(null);
  }, []);

  const loadPerms = useCallback(async () => {
    const requestId = ++permRequestIdRef.current;
    setPermissionsLoading(true);
    try {
      const { data } = await api.get("/roles/permissions");
      if (requestId !== permRequestIdRef.current) return;
      setPermDenied(data.denied || {});
      setPermGranted(data.granted || {});
      setMyMods({ allowed: data.userAllowed || [], denied: data.userDenied || [], keys: data.moduleKeys || [], roleAllowed: data.customAllowed || [], roleDenied: data.customDenied || [] });
      setPermMap(data.effective && Object.keys(data.effective).length ? data.effective : null);
      permLoadWarnedRef.current = false; // 恢复后允许下一次失败再告警
    } catch (e) {
      if (requestId === permRequestIdRef.current && !permLoadWarnedRef.current) {
        permLoadWarnedRef.current = true;
        // 【2026-10-24】过去这里完全静默（catch {}），权限数据拿不到时菜单/按钮悄悄退回
        // 角色名单兜底，用户和管理员都以为「配了权限没生效」。至少留下一条可排查的痕迹。
        console.warn("权限快照加载失败，菜单/按钮暂时退回角色名单兜底", e);
      }
    } finally {
      if (requestId === permRequestIdRef.current) setPermissionsLoading(false);
    }
  }, []);
  // 权限点查询（三态）：true / false = 服务端最终判定；undefined = 快照未加载或键不在目录。
  // 页面用 `permOf(key) ?? 旧角色判断` 接按钮，快照可用即以服务端为准，快照缺失自动退回旧行为。
  const permOf = useCallback((key) => {
    if (permMap && key && Object.prototype.hasOwnProperty.call(permMap, key)) return !!permMap[key];
    return undefined;
  }, [permMap]);
  // 布尔版（默认 false）：给「不允许兜底放行」的场景用（如路由守卫）。
  const canPerm = useCallback((key) => { const v = permOf(key); return v === undefined ? false : v; }, [permOf]);
  // 按钮（动作权限点）查询：canAction("/crm/pool", "assign") ≡ permOf("/crm/pool::assign")。
  const canAction = useCallback((parentKey, action) => permOf(`${parentKey}::${action}`), [permOf]);
  // 优先级：账户级授予 > 账户级拒绝 > 自定义角色授予 > 自定义角色屏蔽 > 基础角色矩阵（授予 / 屏蔽） > 目录默认
  const moduleDenied = useCallback((key) => {
    if (!user) return false;
    const snap = permOf(key);
    if (snap !== undefined) return !snap; // 快照已含全部层与子键继承结果
    if (myMods.allowed.includes(key)) return false;
    if (myMods.denied.includes(key)) return true;
    if ((myMods.roleAllowed || []).includes(key)) return false;
    if ((myMods.roleDenied || []).includes(key)) return true;
    const r = user.baseRole || user.role;
    return (permDenied[r] || []).includes(key);
  }, [user, permDenied, myMods, permOf]);
  const moduleGranted = useCallback((key) => {
    if (!user) return false;
    const snap = permOf(key);
    if (snap !== undefined) return snap;
    if (myMods.allowed.includes(key)) return true;
    if (myMods.denied.includes(key)) return false;
    if ((myMods.roleAllowed || []).includes(key)) return true;
    if ((myMods.roleDenied || []).includes(key)) return false;
    return (permGranted[user.baseRole || user.role] || []).includes(key);
  }, [user, myMods, permGranted, permOf]);
  // 账号语言是跨设备偏好：先从服务器取回，确认完成后才允许写入，避免本机旧缓存抢先覆盖用户刚保存的选择。
  useEffect(() => {
    let alive = true;
    langHydrated.current = !user;
    if (!user) return () => { alive = false; };
    api.get("/users/me/lang").then(({ data }) => {
      if (!alive) return;
      if (data?.lang) setLang(data.lang);
    }).catch(() => {
      // 离线或旧版本服务不可用时保留本机选择；恢复连接后后续手动切换仍会保存。
    }).finally(() => { if (alive) langHydrated.current = true; });
    return () => { alive = false; };
  }, [user, setLang]);
  // 用户调整语言后记录到账号，供下次登录、另一台设备和移动端子页面使用。
  useEffect(() => { if (user && langHydrated.current) api.put("/users/me/lang", { lang }).catch(() => {}); }, [user, lang]);
  // 子功能/栏目可见性: 账户级授予 > 账户级拒绝 > 角色默认(def)
  const featVis = useCallback((key, def) => (moduleGranted(key) ? true : moduleDenied(key) ? false : !!def), [moduleGranted, moduleDenied]);
  const pathDenied = useCallback((pathname) => {
    if (!user) return false;
    if (permMap) {
      // 【2026-10-24】快照可用时改为「目录内白名单」：路径**精确命中**某个页面权限点（最长优先）
      // 即以快照判定为准 —— 管理员屏蔽了页面，直接输 URL 也进不去（旧版只有 /admin/* 拦）。
      // 只做精确匹配（不做前缀）：/board/{pid} 详情、/portal/schedule 等目录外深链维持原放行，
      // 避免误伤「从提醒 / 搜索跳详情」等既有入口；子栏目与按钮的管控仍由接口准入兜底。
      const keys = Object.keys(permMap);
      const exact = keys.filter((k) => !k.includes("::") && pathname === k).sort((a, b) => b.length - a.length)[0];
      if (exact) return !permMap[exact];
      return false;
    }
    const r = user.baseRole || user.role;
    const candidates = new Set([...(myMods.keys || []), ...myMods.allowed, ...myMods.denied, ...(myMods.roleAllowed || []), ...(myMods.roleDenied || []), ...(permDenied[r] || []), ...(permGranted[r] || [])]);
    const best = [...candidates].filter((k) => pathname === k || pathname.startsWith(k + "/")).sort((a, b) => b.length - a.length)[0];
    if (!best) return false;
    if (myMods.allowed.includes(best)) return false;
    if (myMods.denied.includes(best)) return true;
    if ((myMods.roleAllowed || []).includes(best)) return false;
    if ((myMods.roleDenied || []).includes(best)) return true;
    if ((permGranted[r] || []).includes(best)) return false;
    return (permDenied[r] || []).includes(best);
  }, [user, permDenied, permGranted, myMods, permMap]);

  useEffect(() => { localStorage.setItem("ali_lang", lang); }, [lang]);

  const t = useMemo(() => {
    const dict = translations[lang] || translations.en;
    return (k) => dict[k] ?? translations.en[k] ?? k;
  }, [lang]);

  // 工程列表和地图需要及时跟随其他同事的暂停/复工操作，只刷新项目资料，
  // 避免为状态同步反复拉取通知、员工目录和销售目标；失败保留上次有效数据。
  const refreshProjects = useCallback(async () => {
    try { const { data } = await api.get("/projects"); setProjects(data); } catch { /* 下次刷新重试 */ }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [p, r, tg, dir, ps] = await Promise.all([api.get("/projects"), api.get("/reminders"), api.get("/sales-targets"), api.get("/users/directory"), api.get("/phase-standards").catch(() => ({ data: null }))]);
      setPhaseStandards(ps.data);
      setProjects(p.data); setReminders(r.data); setSalesTargets(tg.data || {}); setDirectory(dir.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  // avatar lookup by salesperson name or full name (falls back to null)
  const avatarFor = useCallback((name) => {
    if (!name) return null;
    const u = directory.find((d) => d.salesName === name || d.name === name || d.nameZh === name);
    return u?.avatar || null;
  }, [directory]);

  useEffect(() => {
    const token = authStore.get("ali_token");
    if (token && user) {
      setToken(token); refresh(); loadPerms();
      api.get("/auth/me").then(({ data }) => { setUser(data); authStore.set("ali_user", JSON.stringify(data)); }).catch(() => {});
    }
  }, []); // eslint-disable-line

  // 提醒轮询: 每60秒刷新, 让顶栏铃铛未读角标实时反映付款承诺等新提醒；聊天提醒到点（reminders-changed 事件）立即刷新
  useEffect(() => {
    if (!user) return;
    const pull = () => api.get("/reminders").then(({ data }) => setReminders(data || [])).catch(() => {});
    const iv = setInterval(() => { pull(); loadPerms(); }, 60000);
    window.addEventListener("reminders-changed", pull);
    return () => { clearInterval(iv); window.removeEventListener("reminders-changed", pull); };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username, password, remember = true) => {
    let data;
    try {
      ({ data } = await api.post("/auth/login", { username: (username || "").trim(), password }));
    } catch (e) {
      const st = e?.response?.status;
      const d = e?.response?.data?.detail;
      const detail = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg || "").join(" ") : "";
      return { ok: false, status: st || 0, message: st === 429 || st === 403 ? detail : (!st ? (localStorage.getItem("ali_lang") === "en" ? "Network error — please check your connection and try again" : "网络错误，请检查网络后重试") : "") };
    }
    try {
      authStore.clear();
      authStore.useSession(!remember);
      authStore.set("ali_token", data.token);
      authStore.set("ali_user", JSON.stringify(data.user));
    } catch { /* Safari 隐私模式 / 存储被禁用：仍放行本次会话（内存态） */ }
    resetPermissionSnapshot(true);
    setToken(data.token);
    setUser(data.user);
    // 登录即清空撤销历史：sessionStorage 里可能残留上一个账号（同一标签页换号登录）的快照，
    // 若不清空，新账号会看到并可能误撤别人的操作记录 —— 属于跨账号越权风险，必须重置。
    setHistory([]);
    refresh();
    loadPerms();
    return { ok: true };
  };

  const logout = (broadcast = true) => {
    if (broadcast) broadcastLogout(user?.username);
    if (broadcast) authStore.clear();
    else clearAuthStorageForUser(user?.username);
    resetPermissionSnapshot(false);
    setToken(null); setUser(null); setProjects([]); setReminders([]); setHistory([]);
  };

  useEffect(() => {
    const onStorage = (event) => { if (isLogoutForUser(event, user?.username)) logout(false); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user?.username]); // eslint-disable-line react-hooks/exhaustive-deps

  // 代操作：切换进分包商账号（备份自己的会话 → 换令牌）/ 返回自己的账号
  const IMP_KEY = "ali_imp_backup";
  const impersonate = async (username) => {
    const { data } = await api.post("/auth/impersonate", { username });
    if (!authStore.get(IMP_KEY)) authStore.set(IMP_KEY, JSON.stringify({ token: authStore.get("ali_token"), user: authStore.get("ali_user") }));
    authStore.set("ali_token", data.token); authStore.set("ali_user", JSON.stringify(data.user));
    resetPermissionSnapshot(true);
    setToken(data.token); setUser(data.user); setProjects([]); setReminders([]); setHistory([]);
    await refresh(); loadPerms();
    return data.user;
  };
  const stopImpersonation = async () => {
    let bk = null, recap = null;
    try { recap = (await api.post("/auth/impersonate/end")).data; } catch { recap = null; }  // 会话小结 → 操作者铃铛
    try { bk = JSON.parse(authStore.get(IMP_KEY)); } catch { bk = null; }
    authStore.set(IMP_KEY, ""); localStorage.removeItem(IMP_KEY); sessionStorage.removeItem(IMP_KEY);
    if (!bk?.token) { logout(); return false; }
    authStore.set("ali_token", bk.token); authStore.set("ali_user", bk.user);
    resetPermissionSnapshot(true);
    setToken(bk.token); setUser(JSON.parse(bk.user)); setProjects([]); setReminders([]); setHistory([]);
    await refresh(); loadPerms();
    api.get("/auth/me").then(({ data }) => { setUser(data); authStore.set("ali_user", JSON.stringify(data)); }).catch(() => {});
    return recap || true;
  };
  const impersonating = !!user?.impersonatedBy;

  // 中 → EN → ES → 中
  const toggleLang = () => setLang((l) => LANGS[(LANGS.indexOf(l) + 1) % LANGS.length]);

  const visibleProjects = useMemo(() => {
    if (!user) return [];
    if (user.role === "member" && user.region !== "hq") return projects.filter((p) => inUserTeam(user, p.team));
    return projects;
  }, [projects, user]);

  // 撤销历史栈：每次写操作前压入一个「状态快照 + 操作描述」。
  // 快照只覆盖 projects / reminders 两个集合（与后端 /projects/bulk-restore 的能力一致）；
  // meta 则用于在「操作展示弹窗」里回显这次操作是什么（三语兜底文案，供后期拓展更多操作类型）。
  const snapshot = (meta) => setHistory((h) => [...h.slice(-14), {
    projects: JSON.parse(JSON.stringify(projects)),
    reminders: JSON.parse(JSON.stringify(reminders)),
    meta: meta || null,
  }]);
  // 历史变化即同步写入 sessionStorage，保证刷新后不丢。
  // 写入失败（如存储超限/被禁用）静默忽略，不影响主流程。
  useEffect(() => {
    try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch { /* ignore */ }
  }, [history]);
  const canUndo = history.length > 0;

  // 撤销到指定历史项（index 为 history 下标）：
  // 回滚到该项快照保存的「操作前状态」，并丢弃该项及之后的所有记录（保持栈的时序语义）。
  // 支撑「任意一条都可撤」：点第 k 条 = 一步退回第 k 次操作发生之前。
  const undoTo = async (index) => {
    const entry = history[index];
    if (!entry) return;
    setHistory((h) => h.slice(0, index));
    setProjects(entry.projects); setReminders(entry.reminders);
    // 后端只认识 projects / reminders 两个字段；meta 是纯前端展示信息，需剔除后再提交。
    try { await api.post("/projects/bulk-restore", { projects: entry.projects, reminders: entry.reminders }); } catch {}
    await refresh();
  };
  // 兼容旧调用：等价于撤销最近一次操作（栈顶）。
  const undo = () => undoTo(history.length - 1);

  // Mutations (snapshot -> call API -> refresh)
  // 说明：run 的第二个参数 meta 描述本次操作（三语 + 阶段文案），仅用于在操作展示里回显，不影响写逻辑。
  const run = async (fn, meta) => { snapshot(meta); try { await fn(); } finally { await refresh(); } };
  // 阶段展示：把 PHASES 的 index 翻译成三语文案，供描述里「推进到 xxx」使用
  const phaseName = (idx) => {
    const p = PHASES[idx];
    return p ? { zh: p.zh, en: p.en, es: p.en } : { zh: `阶段${idx}`, en: `Stage ${idx}`, es: `Etapa ${idx}` };
  };

  // 推进 / 完工（「完成本阶段」「竣工」按钮共用）。
  // 需求背景（2026-10-13）：原实现走 run()，而 run 在 finally 里 refresh() 全量重拉 /projects，
  //   使 /board 施工进度列表因 phaseIndex 变化整体重排 —— 刚点过的行会跳到别处，滚动位置也跟着乱。
  // 现改为「局部更新」：POST 成功后只把后端返回的阶段字段合并回当前 projects，不再重拉全量。
  //   - 收益：全站其它页面（Dashboard 等）仍能从 projects 感知该项目阶段变化，且列表顺序不受影响；
  //   - 列表是否重排交由 /board 侧自行冻结（见 TeamBoard 的 pinOrder）。
  // 关键取舍：只取 phaseIndex / phase / payments / phaseHistory 四个字段，不整体替换后端返回值——
  //   GET /projects 列表用投影去掉了 qbLedger（项目流水明细，约占列表 1/3 体积），
  //   而 advance 的 find_one 未做投影会把它带回来，整体替换会让前端白白多背一份大字段。
  //   另外后端 advance 已按现金模式脱敏（cash_mode.mask_project），故 payments 可安全合并。
  const advancePhase = async (id) => {
    const p = projects.find((x) => x.id === id);
    // phaseIndex 从 0 起，目标阶段即当前 +1；from/to 仅用于撤销历史里的操作文案回显
    const from = phaseName(p?.phaseIndex ?? 0);
    const to = phaseName((p?.phaseIndex ?? 0) + 1);
    snapshot({
      kind: "advance_phase",
      zh: `将项目「${p?.address || id}」从「${from.zh}」推进到「${to.zh}」`,
      en: `Advanced "${p?.address || id}" from "${from.en}" to "${to.en}"`,
      es: `Avanzó «${p?.address || id}» de «${from.es}» a «${to.es}»`,
    });
    const { data: updated } = await api.post(`/projects/${id}/advance`);
    if (updated?.id) {
      setProjects((ps) => ps.map((x) => (x.id === id ? {
        ...x,
        phaseIndex: updated.phaseIndex ?? x.phaseIndex,
        phase: updated.phase ?? x.phase,
        ...(updated.payments ? { payments: updated.payments } : {}),
        ...(updated.phaseHistory ? { phaseHistory: updated.phaseHistory } : {}),
      } : x)));
    }
    return updated;
  };
  const transferProject = (id, team) => {
    const p = projects.find((x) => x.id === id);
    const tz = TEAMS.find((t) => t.id === team), tf = TEAMS.find((t) => t.id === p?.team);
    return run(() => api.post(`/projects/${id}/transfer`, { team }), {
      kind: "transfer_project",
      zh: `将项目「${p?.address || id}」从「${tf?.zh || p?.team || "-"}」改派到「${tz?.zh || team}」`,
      en: `Reassigned "${p?.address || id}" from "${tf?.name || p?.team || "-"}" to "${tz?.name || team}"`,
      es: `Reasignó «${p?.address || id}» de «${tf?.name || p?.team || "-"}» a «${tz?.name || team}»`,
    });
  };
  const notifyCollection = (project, stage) => run(() => api.post(`/projects/${project.id}/notify-collection`, { stage }), {
    kind: "notify_collection",
    zh: `通知财务跟进项目「${project?.address || project?.id}」的收款（${stage}）`,
    en: `Notified finance to collect for "${project?.address || project?.id}" (${stage})`,
    es: `Notificó a finanzas cobrar de «${project?.address || project?.id}» (${stage})`,
  });
  const setContract = (id, total, ratios) => {
    const p = projects.find((x) => x.id === id);
    return run(() => api.post(`/projects/${id}/contract`, { total, ratios }), {
      kind: "set_contract",
      zh: `将项目「${p?.address || id}」的合同额设为 ${total}`,
      en: `Set contract amount of "${p?.address || id}" to ${total}`,
      es: `Fijó el monto del contrato de «${p?.address || id}» en ${total}`,
    });
  };
  const markInvoiced = (project, pay) => run(() => api.post(`/projects/${project.id}/payment`, { stage: pay.stage, status: "invoiced" }), {
    kind: "mark_invoiced",
    zh: `将项目「${project?.address || project?.id}」的「${pay.stage}」标记为已开票`,
    en: `Marked "${pay.stage}" of "${project?.address || project?.id}" as invoiced`,
    es: `Marcó «${pay.stage}» de «${project?.address || project?.id}» como facturado`,
  });
  const markPaid = (project, pay, extra) => run(() => api.post(`/projects/${project.id}/payment`, { stage: pay.stage, status: "paid", ...(extra || {}) }), {
    kind: "mark_paid",
    zh: `将项目「${project?.address || project?.id}」的「${pay.stage}」标记为已收款`,
    en: `Marked "${pay.stage}" of "${project?.address || project?.id}" as paid`,
    es: `Marcó «${pay.stage}» de «${project?.address || project?.id}» como pagado`,
  });
  const createProject = async (data) => {
    let out;
    await run(async () => { const { data: p } = await api.post("/projects", data); out = p; }, {
      kind: "create_project",
      zh: `新建项目「${data?.address || ""}」`,
      en: `Created project "${data?.address || ""}"`,
      es: `Creó el proyecto «${data?.address || ""}»`,
    });
    return out;
  };
  const startConstruction = (id) => {
    const p = projects.find((x) => x.id === id);
    return run(() => api.post(`/projects/${id}/start-construction`), {
      kind: "start_construction",
      zh: `将项目「${p?.address || id}」从设计阶段进入施工阶段`,
      en: `Moved "${p?.address || id}" from design into construction`,
      es: `Movió «${p?.address || id}» de diseño a construcción`,
    });
  };
  const setSalesTarget = async (salesName, body) => {
    await api.put(`/sales-targets/${encodeURIComponent(salesName)}`, body);
    const { data } = await api.get("/sales-targets");
    setSalesTargets(data || {});
  };
  const updateProject = (id, patch) => {
    const p = projects.find((x) => x.id === id);
    // 目前仅「检查日期」这一个字段走 updateProject（ProjectDialog 的 inspection-date-input）；
    // 后续若扩展到更多字段，可在此按 patch 的键拼装更细的描述。
    const keys = Object.keys(patch || {});
    return run(() => api.patch(`/projects/${id}`, patch), {
      kind: "update_project",
      zh: `更新项目「${p?.address || id}」的字段：${keys.join("、") || "-"}`,
      en: `Updated "${p?.address || id}" fields: ${keys.join(", ") || "-"}`,
      es: `Actualizó campos de «${p?.address || id}»: ${keys.join(", ") || "-"}`,
    });
  };
  const deleteProject = (id) => {
    const p = projects.find((x) => x.id === id);
    return run(() => api.delete(`/projects/${id}`), {
      kind: "delete_project",
      zh: `删除项目「${p?.address || id}」（移入回收站）`,
      en: `Deleted "${p?.address || id}" (moved to recycle bin)`,
      es: `Eliminó «${p?.address || id}» (a la papelera)`,
    });
  };
  const markReminderRead = (id) => {
    const r = reminders.find((x) => x.id === id);
    return run(() => api.post(`/reminders/${id}/read`), {
      kind: "mark_reminder_read",
      zh: `将提醒「${r?.title || r?.text || id}」标记为已读`,
      en: `Marked reminder "${r?.title || r?.text || id}" as read`,
      es: `Marcó el recordatorio «${r?.title || r?.text || id}» como leído`,
    });
  };
  // 批量已读（用户已看到的提醒）：只改本地状态，不触发全量 refresh，避免列表闪动
  const markRemindersRead = async (ids) => {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return 0;
    const set = new Set(list);
    setReminders((rs) => rs.map((r) => (set.has(r.id) ? { ...r, read: true } : r)));
    try { const { data } = await api.post("/reminders/read-batch", { ids: list }); return data?.count || 0; }
    catch { return 0; }
  };
  const markAllRemindersRead = async (projectId) => {
    const { data } = await api.post("/reminders/read-all", projectId ? { projectId } : {});
    setReminders((rs) => rs.map((r) => (r.read || (projectId && r.projectId !== projectId) ? r : { ...r, read: true })));
    return data?.count || 0;
  };
  // 头像：{ fileId }（裁剪方图已分片上传到对象存储）或旧 base64 字符串
  const updateAvatar = async (payload) => {
    const { data } = await api.put("/users/me/avatar", typeof payload === "string" ? { avatar: payload } : payload);
    authStore.set("ali_user", JSON.stringify(data));
    setUser(data);
    try { const dir = await api.get("/users/directory"); setDirectory(dir.data || []); } catch {}
  };
  const changePassword = async (currentPassword, newPassword) => {
    const { data } = await api.post("/auth/change-password", { currentPassword, newPassword });
    if (data.token) { authStore.set("ali_token", data.token); setToken(data.token); }
    authStore.set("ali_user", JSON.stringify(data.user));
    setUser(data.user);
  };
  // 现金开关（IT 管理员 / CEO）：关闭后现金收付不可登记且从统计/列表屏蔽
  const [cashMode, setCashModeState] = useState({ enabled: true, canToggle: false });
  const refreshCashMode = useCallback(async () => {
    try { const { data } = await api.get("/finance/cash-mode"); setCashModeState(data); } catch { /* ignore */ }
  }, []);
  // 管理页传入仅存在页面内存的短期解锁凭据；全局仅保留业务启停值，避免泄露管理详情。
  const updateCashMode = async (enabled, unlockToken) => {
    const { data } = await api.put("/finance/cash-mode", { enabled }, { headers: { "X-Cash-Mode-Token": unlockToken } });
    setCashModeState({ enabled: data.enabled, canToggle: false });
    refresh();
    return data;
  };
  useEffect(() => { if (user && user.role !== "subcontractor") refreshCashMode(); }, [user, refreshCashMode]);
  // 持卡人：是否绑定了公司信用卡（决定「我的信用卡」菜单）
  const [myCardCount, setMyCardCount] = useState(0);
  useEffect(() => {
    if (!user || user.role === "subcontractor") { setMyCardCount(0); return; }
    api.get("/finance/credit-cards/mine").then(({ data }) => setMyCardCount((data?.cards || []).length)).catch(() => setMyCardCount(0));
  }, [user]);

  const value = {
    user, lang, t, login, logout, toggleLang, setLang, i18nTick,
    impersonate, stopImpersonation, impersonating,
    projects, visibleProjects, allProjects: projects, reminders, loading, refresh, refreshProjects,
    advancePhase, transferProject, notifyCollection, setContract,
    markInvoiced, markPaid, createProject, updateProject, markReminderRead, markRemindersRead, markAllRemindersRead, startConstruction,
    deleteProject,
    salesTargets, setSalesTarget, updateAvatar, changePassword,
    directory, avatarFor,
    moduleDenied, moduleGranted, featVis, pathDenied, loadPerms,
    permMap, permissionsLoading, permOf, canPerm, canAction,
    cashMode, setCashMode: updateCashMode, refreshCashMode, myCardCount,
    undo, canUndo, snapshot, api, history, undoTo,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
